// What the AI integration page shows BEFORE anything is created: which source
// field fills which part of the Signal, what Signals the mapping really makes
// from the data, and which of those already exist in one of the user's
// templates (reuse) or need a new one. Pure functions - the screen only renders.
import { matchExistingSignalName } from './autoMapping';
import { resolvePath, type JsonValue, type MappingRule, type MappingSpec } from './mappingEngine';
import { previewMapping, type PreviewSignal } from './mappingPreview';
import { producedSignalNames } from './signalTemplate';
import type { Template } from '../types';

export interface ReviewRow {
  to: string;
  title: string; // plain words: "Value", "Time", "Label · host"
  source: string; // "field: val" | "fixed text: cpu"
  example: string; // from the first record
  how: string; // "as number", "unix seconds → time"...
}

export type SignalStatus = 'existing' | 'similar' | 'new';

export interface ReviewSignal {
  name: string;
  status: SignalStatus;
  template?: string; // nickname of the template that has it (existing)
  templateTitle?: string;
  similarTo?: string; // an existing Signal that looks like it (similar)
  similarTemplate?: string;
  similarTemplateTitle?: string;
  renamable: boolean; // the name is a fixed text rule, so it can be swapped for the existing one
}

export interface Review {
  rows: ReviewRow[];
  // How the value will be presented (type/unit and the measured period) - from spec.calc.
  presentation: string[];
  signals: ReviewSignal[];
  preview: PreviewSignal[];
}

const TYPE_WORDS: Record<string, string> = {
  currency: 'an amount of money',
  percentage: 'a percentage',
  percentageRatio: 'a percentage (a 0-1 fraction shown as %)',
  count: 'a count',
  duration: 'a duration',
  number: 'a number',
};

// Plain-words lines about how spec.calc will present the value.
export function describeCalc(spec: MappingSpec): string[] {
  const calc = spec.calc as { value?: { type?: string; unit?: string; unitPath?: string }; period?: { startPath?: string; endPath?: string } } | undefined;
  if (!calc?.value) return [];
  const v = calc.value;
  const out: string[] = [];
  const unit = v.unit ? ` in ${v.unit}` : v.unitPath ? ` (currency read from ${v.unitPath})` : '';
  const example = v.type === 'currency' && v.unit === 'USD' ? ', e.g. $53.27B' : '';
  out.push(`Shown as ${TYPE_WORDS[v.type ?? ''] ?? v.type ?? 'a value'}${unit}${example}.`);
  if (calc.period?.endPath) {
    out.push(`Measured period from ${[calc.period.startPath, calc.period.endPath].filter(Boolean).join(' to ')} - kept separate from when Zmist received it.`);
  }
  return out;
}

const TITLES: Record<string, string> = { name: 'Signal name', value: 'Value', at: 'Time' };

function rowTitle(to: string): string {
  if (TITLES[to]) return TITLES[to];
  if (to.startsWith('labels.')) return `Label · ${to.slice(7)}`;
  return to;
}

function howText(r: MappingRule): string {
  const parts: string[] = [];
  if (r.cast) parts.push(`as ${r.cast}`);
  if (typeof r.unitScale === 'number') parts.push(`× ${r.unitScale}`);
  if (r.timestamp) parts.push(`time format ${r.timestamp}`);
  return parts.join(', ');
}

function show(v: JsonValue | undefined): string {
  if (v === undefined || v === null) return '';
  return typeof v === 'object' ? JSON.stringify(v) : String(v);
}

// Which name rule (if any) gives every Signal the same fixed name.
export function constName(spec: MappingSpec): string | undefined {
  const rule = (spec.rules ?? []).find((r) => r.to === 'name' && typeof r.const === 'string' && r.const);
  return rule ? String(rule.const) : undefined;
}

// Swaps the fixed Signal name for an existing one (the "use the existing Signal" choice).
export function renameConstName(spec: MappingSpec, to: string): MappingSpec {
  return { ...spec, rules: (spec.rules ?? []).map((r) => (r.to === 'name' && typeof r.const === 'string' ? { ...r, const: to } : r)) };
}

// extraNames: the Signal names of an "all metrics" integration (one per list) -
// used instead of the names the single previewed list happens to produce.
export function buildReview(spec: MappingSpec, records: JsonValue[], templates: Template[], extraNames?: string[]): Review {
  const first = records[0];
  const rows: ReviewRow[] = (spec.rules ?? [])
    .filter((r): r is MappingRule & { to: string } => !!r.to)
    .map((r) => ({
      to: r.to,
      title: rowTitle(r.to),
      source: r.from ? `field: ${r.from}` : `fixed text: ${show(r.const as JsonValue)}`,
      example: r.from && first !== undefined ? show(resolvePath(first, r.from)) : '',
      how: howText(r),
    }));

  const results = previewMapping(spec, records.slice(0, 20), 'signal');
  const preview = results
    .flatMap((x) => x.outputs)
    .map((o) => o.signal)
    .filter((s): s is PreviewSignal => !!s)
    .slice(0, 5);

  const byName = new Map<string, Template>();
  for (const t of templates) for (const sg of t.signals) if (!byName.has(sg.name.toLowerCase())) byName.set(sg.name.toLowerCase(), t);
  const allNames = [...new Set(templates.flatMap((t) => t.signals.map((sg) => sg.name)))];
  const fixed = constName(spec);

  const names = extraNames ? [...new Set(extraNames)] : producedSignalNames(spec, results);
  const signals: ReviewSignal[] = names.map((name) => {
    const hit = byName.get(name.toLowerCase());
    if (hit) return { name, status: 'existing', template: hit.nickname, templateTitle: hit.title || hit.nickname, renamable: false };
    const near = matchExistingSignalName(name, allNames);
    const nearT = near ? byName.get(near.toLowerCase()) : undefined;
    if (near && nearT) {
      return {
        name,
        status: 'similar',
        similarTo: near,
        similarTemplate: nearT.nickname,
        similarTemplateTitle: nearT.title || nearT.nickname,
        renamable: !extraNames && fixed === name,
      };
    }
    return { name, status: 'new', renamable: false };
  });
  return { rows, presentation: describeCalc(spec), signals, preview };
}
